import React, { useState, useRef } from 'react';
import {
  Camera,
  Save,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  Key,
  RefreshCw,
  Copy,
  CheckCheck,
  Eye,
  EyeOff,
  Shield,
  Trash,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { usersAPI } from '../../services/api';
import MessageModal from '../../components/common/MessageModal';
import './Profile.css';
import { formatDate } from '../../utils/dateFormatter';
import '../Settings/Settings.css';
import { getStaticUrl } from '../../constants/config';

const Profile = () => {
  const { currentUser, setCurrentUser, showNotification } = useAppContext();
  const [form, setForm] = useState({ ...currentUser });
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    newPass: '',
    confirm: '',
  });
  const [activeTab, setActiveTab] = useState('info');
  const [tokenVisible, setTokenVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState('');
  const [loadingSaveProfile, setLoadingSaveProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  const showTokenTab = currentUser.role === 'Trainee' || currentUser.role === 'Instructor';
  const joinDate = currentUser.joinDate || currentUser.joined || 'N/A';
  const formattedJoinDate = joinDate !== 'N/A' ? formatDate(joinDate) : 'N/A';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoadingSaveProfile(true);

    try {
      const updateData = {
        firstName: form.firstName || '',
        lastName: form.lastName || '',
        username: form.username || '',
        phone: form.phone || '',
        department: form.department,
        location: form.location || '',
      };
      // Only include email if user is Administrator
      if (currentUser.role === 'Administrator') {
        updateData.email = form.email;
      }

      await usersAPI.updateUser(currentUser.id, updateData);
      const updatedUser = {
        ...currentUser,
        ...updateData,
        name: `${updateData.firstName} ${updateData.lastName}`.trim() || currentUser.name
      };

      setCurrentUser(updatedUser);
      setForm(updatedUser);
      showNotification('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification(
        error.response?.data?.message || 'Failed to update profile. Please try again.',
        'error'
      );
      setForm({ ...currentUser });
    } finally {
      setLoadingSaveProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPass !== passwordForm.confirm) {
      showNotification('Passwords do not match', 'error');
      return;
    }

    if (passwordForm.newPass.length < 6) {
      showNotification('Password must be at least 6 characters long', 'error');
      return;
    }

    setLoadingPassword(true);

    try {
      await usersAPI.updatePassword(
        currentUser.id,
        passwordForm.current,
        passwordForm.newPass
      );

      setPasswordForm({ current: '', newPass: '', confirm: '' });
      showNotification('Password changed successfully!', 'success');
    } catch (error) {
      console.error('Error changing password:', error);
      showNotification(
        error.response?.data?.message || 'Failed to change password. Please try again.',
        'error'
      );
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(currentUser.token).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showNotification('Token copied to clipboard', 'info');
  };

  const handleRegenerateToken = async () => {
    setLoadingToken(true);

    try {
      const response = await usersAPI.updateToken(currentUser.id);
      const updatedUser = {
        ...currentUser,
        token: response.data.data.token
      };

      setCurrentUser(updatedUser);
      showNotification('Token regenerated successfully!', 'success');
    } catch (error) {
      console.error('Error regenerating token:', error);
      showNotification(
        error.response?.data?.message || 'Failed to regenerate token. Please try again.',
        'error'
      );
    } finally {
      setLoadingToken(false);
    }
  };

  const handleEmailClick = (email) => {
    setMessageRecipient(email);
    setMessageModalOpen(true);
  };

  const handleSendMessage = ({ recipientEmail, subject, message }) => {
    showNotification(`Message sent to ${recipientEmail} successfully!`, 'success');
    setMessageModalOpen(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('Invalid file type. Please select an image (JPEG, PNG, GIF, WebP).', 'error');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('File size too large. Maximum 5MB.', 'error');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setAvatarPreview(event.target.result);
    };
    reader.readAsDataURL(file);

    // Upload
    handleAvatarUpload(file);
  };

  const handleAvatarUpload = async (file) => {
    setAvatarLoading(true);
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await usersAPI.uploadAvatar(currentUser.id, formData);
      const updatedUser = {
        ...currentUser,
        avatar: getStaticUrl(response.data.data?.thumbnail || response.data.data?.avatar_url || response.data.data?.avatar),
        avatar_url: response.data.data?.avatar_url || response.data.data?.avatar,
        thumbnail: response.data.data?.thumbnail
      };
      setCurrentUser(updatedUser);
      setForm(updatedUser);
      setAvatarPreview(null);
      showNotification('Profile photo updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      showNotification(
        error.response?.data?.message || 'Failed to upload profile photo. Please try again.',
        'error'
      );
    } finally {
      setAvatarLoading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAvatarDelete = async () => {
    if (!currentUser.avatar_url && !currentUser.avatar) {
      showNotification('No profile photo to delete.', 'info');
      return;
    }

    if (!window.confirm('Are you sure you want to delete your profile photo?')) {
      return;
    }

    setAvatarLoading(true);
    try {
      const response = await usersAPI.deleteAvatar(currentUser.id);
      const updatedUser = {
        ...currentUser,
        avatar: null,
        avatar_url: null,
        thumbnail: null
      };
      setCurrentUser(updatedUser);
      setForm(updatedUser);
      showNotification('Profile photo deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting avatar:', error);
      showNotification(
        error.response?.data?.message || 'Failed to delete profile photo. Please try again.',
        'error'
      );
    } finally {
      setAvatarLoading(false);
    }
  };

  const tabs = [
    { id: 'info', label: 'Personal Info', icon: User },
    { id: 'password', label: 'Change Password', icon: Key },
  ];
  if (showTokenTab) {
    tabs.push({ id: 'token', label: 'Access Token', icon: Shield });
  }

  const initials = (form.firstName && form.lastName)
    ? (form.firstName[0] + form.lastName[0]).toUpperCase().slice(0, 2)
    : form.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="profile">
      {/* Profile Header */}
      <div className="profile__header card">
        <div className="profile__avatar-wrapper">
          <div className="profile__avatar">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Preview" />
            ) : currentUser.avatar ? (
              <img src={currentUser.avatar} alt={currentUser.name} />
            ) : (
              <span>{initials}</span>
            )}
            {currentUser.avatar && (
              <button
                className="profile__avatar-delete"
                onClick={handleAvatarDelete}
                disabled={avatarLoading}
                title="Delete photo"
              >
                <Trash size={12} />
              </button>
            )}
          </div>
          <button
            className="profile__avatar-btn"
            onClick={handleAvatarClick}
            disabled={avatarLoading}
            title="Upload photo"
          >
            {avatarLoading ? (
              <div className="spinner-small"></div>
            ) : (
              <Camera size={16} />
            )}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
          />
        </div>
        <div className="profile__header-info">
          <h2 className="profile__header-name">{currentUser.name}</h2>
          <p className="profile__header-role">{currentUser.role} · {currentUser.department}</p>
          <button
            className="profile__header-email link"
            onClick={() => handleEmailClick(currentUser.email)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            {currentUser.email}
          </button>
          <div className="profile__header-badges">
            <span className="badge badge-green">Active</span>
            <span className="badge badge-dark">Since {formattedJoinDate}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile__tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`profile__tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {activeTab === 'info' && (
          <form className="card profile__form" onSubmit={handleSaveProfile}>
            <div className="card-header">
              <h3>Personal Information</h3>
              <p className="text-sm text-muted">Update your profile details below</p>
            </div>
            <div className="card-body">
              <div className="profile__form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <User size={13} style={{ display: 'inline', marginRight: 6 }} />
                    First Name
                  </label>
                  <input
                    className="form-input"
                    name="firstName"
                    value={form.firstName || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Last Name
                  </label>
                  <input
                    className="form-input"
                    name="lastName"
                    value={form.lastName || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <User size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Username
                  </label>
                  <input
                    className="form-input"
                    name="username"
                    value={form.username || ''}
                    onChange={handleChange}
                    placeholder="e.g., sophiewilliams"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Mail size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Email Address
                  </label>
                  <input
                    className="form-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    readOnly={currentUser.role !== 'Administrator'}
                    disabled={currentUser.role !== 'Administrator'}
                    style={currentUser.role !== 'Administrator' ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                  />
                  {currentUser.role !== 'Administrator' && (
                    <p className="text-xs text-muted" style={{ marginTop: '4px' }}>
                      Only administrators can change email addresses
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Phone size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Phone Number
                  </label>
                  <input
                    className="form-input"
                    name="phone"
                    value={form.phone || ''}
                    onChange={handleChange}
                    placeholder="+234 XXX XXX XXXX"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Building size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Department
                  </label>
                  <input
                    className="form-input"
                    name="department"
                    value={form.department}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <MapPin size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Location
                  </label>
                  <input
                    className="form-input"
                    name="location"
                    value={form.location || ''}
                    onChange={handleChange}
                    placeholder="e.g., Bonny Island, Rivers State"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Calendar size={13} style={{ display: 'inline', marginRight: 6 }} />
                    Role
                  </label>
                  <input
                    className="form-input"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    readOnly
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                </div>
              </div>

              <div className="profile__form-actions">
                <button type="button" className="btn btn-ghost" disabled={loadingSaveProfile}>
                  Discard Changes
                </button>
                <button type="submit" className="btn btn-primary" disabled={loadingSaveProfile}>
                  <Save size={15} />
                  {loadingSaveProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'password' && (
          <form className="card profile__form" onSubmit={handleChangePassword}>
            <div className="card-header">
              <h3>Change Password</h3>
              <p className="text-sm text-muted">Ensure your account stays secure</p>
            </div>
            <div className="card-body">
              <div className="profile__password-form">
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input
                    className="form-input"
                    type="password"
                    name="current"
                    value={passwordForm.current}
                    onChange={handlePasswordChange}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    className="form-input"
                    type="password"
                    name="newPass"
                    value={passwordForm.newPass}
                    onChange={handlePasswordChange}
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    className="form-input"
                    type="password"
                    name="confirm"
                    value={passwordForm.confirm}
                    onChange={handlePasswordChange}
                    placeholder="Re-enter new password"
                    required
                  />
                </div>

                <div className="profile__password-hints">
                  <h4>Password Requirements</h4>
                  <ul>
                    <li>At least 6 characters long</li>
                    <li>Contains uppercase and lowercase letters</li>
                    <li>Contains at least one number</li>
                    <li>Contains at least one special character</li>
                  </ul>
                </div>

                <div className="profile__form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loadingPassword}>
                    <Key size={15} />
                    {loadingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'token' && (
          <div className="card profile__form">
            <div className="card-header">
              <h3>VR Access Token</h3>
              <p className="text-sm text-muted">Your personal token for VR device authentication</p>
            </div>
            <div className="card-body">
              <div className="settings__token-info">
                <div className="settings__token-info-icon">
                  <Shield size={20} />
                </div>
                <div>
                  <h4>VR Device Login Token</h4>
                  <p>
                    This 4-character alphanumeric token is used to authenticate from your VR headset.
                    Keep it secure and regenerate if compromised.
                  </p>
                </div>
              </div>

              <div className="settings__token-value" style={{ marginTop: 'var(--space-6)' }}>
                <div className="settings__token-display">
                  {tokenVisible ? (
                    <span className="settings__token-chars">{currentUser.token}</span>
                  ) : (
                    <span className="settings__token-hidden">••••</span>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => setTokenVisible(!tokenVisible)}
                  >
                    {tokenVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>

              <div className="profile__form-actions" style={{ marginTop: 'var(--space-6)' }}>
                <button
                  className="btn btn-ghost"
                  onClick={handleCopyToken}
                  disabled={loadingToken}
                >
                  {copied ? <CheckCheck size={15} /> : <Copy size={15} />}
                  {copied ? ' Copied!' : ' Copy Token'}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleRegenerateToken}
                  disabled={loadingToken}
                >
                  <RefreshCw size={15} className={loadingToken ? 'settings__spin' : ''} />
                  {loadingToken ? ' Generating...' : ' Regenerate Token'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        recipientEmail={messageRecipient}
        currentUserRole={currentUser?.role}
        onSend={handleSendMessage}
      />
    </div>
  );
};

export default Profile;
